/**
 * Submission types + formatter for the in-chat interactive widget system.
 *
 * Interactive widgets render Heidi-side as native React components via
 * ``InteractiveWidgetBlock``'s widget registry. When the user finishes
 * the embedded flow (or skips it), the widget hands the parent chat a
 * typed submission payload (one variant per ``kind``); the chat formats
 * it with ``formatInteractiveWidgetSubmission`` into a natural-language
 * follow-up message and sends it through the normal ``handleSendMessage``
 * pipeline so the LLM sees the user's answers as the next chat turn.
 *
 * Adding a new widget kind = add a new variant to
 * ``InteractiveWidgetSubmission`` and a matching arm in
 * ``formatInteractiveWidgetSubmission``. The discriminated union keeps
 * the formatter switch exhaustively type-checked: dropping a kind on the
 * BE without a matching FE variant surfaces as a compile error.
 */

// ── VisualDx Diagnostic Flow ───────────────────────────────────────

/** Multi-select body region IDs collected by the diagnostic flow. */
export type VisualDxRegionId =
  | 'face'
  | 'scalp'
  | 'neck'
  | 'chest'
  | 'trunk'
  | 'arm'
  | 'forearm'
  | 'hand'
  | 'leg'
  | 'foot'
  | 'genitals'
  | 'other';

/** Multi-select morphology IDs collected by the diagnostic flow. */
export type VisualDxMorphologyId =
  | 'widespreadItching'
  | 'urticarialWheals'
  | 'erythematousMacules'
  | 'scalyPlaques'
  | 'vesiclesBullae'
  | 'nocturnalItchWorsens'
  | 'newMedicationLast2Weeks'
  | 'palpablePurpura';

export type VisualDxAgeBandId =
  | 'infant'
  | 'child'
  | 'adolescent'
  | 'adult'
  | 'olderAdult';

export type VisualDxOnsetSpeed =
  | 'rapidly'
  | 'acutely'
  | 'chronically'
  | 'recurring'
  | 'presentAtBirth';

/** Either an age band, an exact age in years, or null when skipped. */
export type VisualDxAgeSelection =
  | { band: VisualDxAgeBandId }
  | { exactYears: number }
  | null;

/**
 * Selections collected by ``VisualDxDiagnosticFlow``. Field shapes mirror
 * the inline component's local state — when extending, change them in
 * lock-step with the component to keep the typed handoff exhaustive.
 */
export interface VisualDxDiagnosticFlowSelections {
  /** Which side of the body the user was viewing on the region step. */
  bodyAspect: 'front' | 'back';
  /** Body regions the user tapped (multi-select, may be empty if skipped). */
  regions: VisualDxRegionId[];
  /** Morphology findings ticked (multi-select, may be empty if skipped). */
  morphology: VisualDxMorphologyId[];
  /** Patient age — band, exact value, or null when skipped without one. */
  age: VisualDxAgeSelection;
  /** Onset speed (single-select, null when skipped). */
  onset: VisualDxOnsetSpeed | null;
  /** True iff the user clicked "Skip to the Results" instead of completing all steps. */
  skipped: boolean;
}

// ── Discriminated union over every interactive-widget submission ─────

export type InteractiveWidgetSubmission = {
  kind: 'visualdx_diagnostic_flow';
  selections: VisualDxDiagnosticFlowSelections;
};

// ── Display labels for natural-language formatting ───────────────────

const REGION_LABELS: Record<VisualDxRegionId, string> = {
  face: 'Face',
  scalp: 'Scalp',
  neck: 'Neck',
  chest: 'Chest',
  trunk: 'Trunk',
  arm: 'Arm',
  forearm: 'Forearm',
  hand: 'Hand',
  leg: 'Leg',
  foot: 'Foot',
  genitals: 'Genitals',
  other: 'Other',
};

const MORPHOLOGY_LABELS: Record<VisualDxMorphologyId, string> = {
  widespreadItching: 'Widespread itching',
  urticarialWheals: 'Urticarial wheals',
  erythematousMacules: 'Erythematous macules',
  scalyPlaques: 'Scaly plaques',
  vesiclesBullae: 'Vesicles / bullae',
  nocturnalItchWorsens: 'Nocturnal itch worsens',
  newMedicationLast2Weeks: 'New medication in last 2 weeks',
  palpablePurpura: 'Palpable purpura',
};

const AGE_BAND_LABELS: Record<VisualDxAgeBandId, string> = {
  infant: 'Infant (<2 y)',
  child: 'Child (2-12 y)',
  adolescent: 'Adolescent (13-17 y)',
  adult: 'Adult (18-64 y)',
  olderAdult: 'Older adult (65+ y)',
};

const ONSET_LABELS: Record<VisualDxOnsetSpeed, string> = {
  rapidly: 'Rapidly (hours - minutes to <24 h)',
  acutely: 'Acutely (days to a few weeks)',
  chronically: 'Chronically (weeks to months, gradual)',
  recurring: 'Recurring (comes and goes over time)',
  presentAtBirth: 'Present at birth (congenital / lifelong)',
};

const formatAge = (age: VisualDxAgeSelection): string | null => {
  if (age === null) return null;
  if ('exactYears' in age) return `${age.exactYears} years`;
  return AGE_BAND_LABELS[age.band];
};

/**
 * Format a widget submission as a natural-language follow-up message the
 * LLM can ingest as a normal chat turn. Empty / null fields are skipped
 * so a partial (skipped) submission doesn't surface "—" placeholders to
 * the model.
 *
 * The closing instruction is the trigger phrase the LLM's system prompt
 * matches on to know it should call ``render_visualdx_ranked_differential``
 * with its proposed ranked list, rather than chaining another VisualDx
 * lookup.
 */
export const formatInteractiveWidgetSubmission = (
  submission: InteractiveWidgetSubmission
): string => {
  switch (submission.kind) {
    case 'visualdx_diagnostic_flow': {
      const s = submission.selections;
      const lines: string[] = [
        s.skipped
          ? 'I skipped to the results from the VisualDx interactive flow with the following partial selections:'
          : 'I completed the VisualDx interactive flow with the following selections:',
      ];
      lines.push(`- Body aspect viewed: ${s.bodyAspect === 'front' ? 'Front' : 'Back'}`);
      if (s.regions.length) {
        lines.push(`- Regions: ${s.regions.map((id) => REGION_LABELS[id]).join(', ')}`);
      }
      if (s.morphology.length) {
        lines.push(
          `- Findings: ${s.morphology.map((id) => MORPHOLOGY_LABELS[id]).join(', ')}`
        );
      }
      const age = formatAge(s.age);
      if (age) lines.push(`- Patient age: ${age}`);
      if (s.onset) lines.push(`- Onset: ${ONSET_LABELS[s.onset]}`);
      lines.push(
        'Please build a ranked differential of 3-5 likely diagnoses and call render_visualdx_ranked_differential to display it.'
      );
      return lines.join('\n');
    }
  }
};
